import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, ExternalLink, FileText } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";

const STATUS_META = {
  ready:     { pill: "ok",   label: "Ready",     color: "#4F8A3A" },
  attention: { pill: "warn", label: "Attention", color: "#C8932C" },
  critical:  { pill: "err",  label: "Critical",  color: "#C45454" },
};

// Which assessment pillars can be filed in the Audit Vault directly from
// here, and what payload to send. Note: `documentation` is intentionally
// omitted — that pillar maps to TWO vault types (vendor_list + pest_annual)
// and is filed from the Documentation module itself, not from one
// PillarCard button.
const VAULT_TYPE_FOR_PILLAR = {
  hygiene:        "hygiene",
  cleanliness:    "cleanliness",
  vehicle_cleanliness: "vehicle_cleanliness",
  ccp:            "ccp",
  deviations:     "ccp_deviations",
  cooking:        "kitchen_cooking",     // legacy — kept for older tiles
  hot_cold_hold:  "kitchen_hold",         // legacy — kept for older tiles
  daily_kitchen:  "kitchen_daily",
  equipment_care: "equipment_care",
  forms:          "forms",
  fsms_team:      "fsms_team",
  training:       "training_currency",
  calibration:    "calibration",
};

// Per-pillar DRAFT preview endpoint. When a pillar isn't 100% or the
// month is still open, the vault filing button is disabled — but the
// owner still wants to eyeball the report before end-of-month. Any key
// not listed here means "no preview available" (the button will hide).
const PREVIEW_ENDPOINT_FOR_PILLAR = {
  hygiene:       "/reports/compliance/hygiene",
  cleanliness:   "/reports/compliance/cleanliness",
  vehicle_cleanliness: "/reports/compliance/vehicle-cleanliness",
  ccp:           "/reports/compliance/ccp",
  deviations:    "/reports/compliance/ccp_deviations",
  cooking:       "/reports/compliance/kitchen_cooking",
  hot_cold_hold: "/reports/compliance/kitchen_hold",
  daily_kitchen: "/reports/compliance/kitchen_daily",
  equipment_care: "/reports/compliance/equipment_care",
  forms: "/reports/compliance/forms",
};
// Standing types never carry a period_month payload — they capture the
// CURRENT state at snapshot time.
const STANDING_VAULT_TYPES = new Set([
  "fsms_team", "training_currency", "calibration",
]);
// The Incidents pillar is special: there's no single "incidents" vault
// type because each ERR / NCR files independently. Its button fans out
// to /audit-vault/bulk-file-incidents instead.
const BULK_INCIDENTS_PILLAR_KEY = "incidents";
// Pest Control & Approved Vendor pillars each map to their own vault
// type (pest_period / vendor_list) but are filed via dedicated bulk
// endpoints because their inputs differ from the standard
// `/audit-vault/generate` shape. Split out of the old combined
// "documentation" pillar on 2026-06-25 so reworks loop independently.
const BULK_PEST_PILLAR_KEY = "pest_control";
const BULK_VENDOR_PILLAR_KEY = "approved_vendor";

export default function PillarCard({
  pillar, siteId, periodValue, periodMonthForVault,
  bulkPeriodStart, bulkPeriodEnd, onFiled,
}) {
  const meta = STATUS_META[pillar.status] || STATUS_META.ready;
  const { notify, confirm } = useDialog();
  const [vaultBusy, setVaultBusy] = useState(false);

  const isBulkIncidents = pillar.key === BULK_INCIDENTS_PILLAR_KEY;
  const isBulkPest = pillar.key === BULK_PEST_PILLAR_KEY;
  const isBulkVendor = pillar.key === BULK_VENDOR_PILLAR_KEY;
  const isBulk = isBulkIncidents || isBulkPest || isBulkVendor;
  const vaultType = VAULT_TYPE_FOR_PILLAR[pillar.key];
  const vs = pillar.vault_status || null;
  const inVault = !!(vs && vs.filed);
  const isStanding = vaultType && STANDING_VAULT_TYPES.has(vaultType);
  // Monthly-bound pillars need a YYYY-MM period_month for vault filing;
  // standing types do not. Standing snapshots can always be re-filed,
  // even if a prior snapshot exists — they're history, not idempotent.
  const needsPeriod = vaultType && !isStanding;
  const hasPeriod = !!periodMonthForVault;
  const standingAlwaysFileable = isStanding && !!siteId;
  // ISO-22000-grade gate: only file a vault snapshot when the pillar is
  // 100% (no missing artefacts / open NCs). Audit packs containing a
  // half-baked pillar are worse than no pack at all — they signal to an
  // auditor "we know about the gap but filed anyway". Owner / manager
  // can still drill into the module via "Open module" and fix the
  // underlying data, then re-run the assessment.
  //
  // `canFile` is the actual "click is allowed" gate. We still RENDER the
  // button in the disabled state when `!isReady` so users see exactly
  // why they can't file (tooltip + label) rather than a missing CTA.
  const isReady = Math.round(Number(pillar.score) || 0) >= 100;
  const canFile = isReady && (isBulk
    ? (
        // Pest needs a site + period window. Vendor is tenant-wide so
        // it only needs the user to be looking at a site for context
        // (but we still gate on siteId for UI consistency).
        isBulkVendor
          ? !!siteId
          : !!siteId && !!bulkPeriodStart && !!bulkPeriodEnd
      )
    : (
        pillar.status === "ready" && !!vaultType && !!siteId
        && (!needsPeriod || hasPeriod)
        && (standingAlwaysFileable || !inVault)
      ));

  const fileBulkIncidents = async () => {
    if (!canFile || vaultBusy) return;
    const ok = await confirm({
      title: "File all incidents in the Audit Vault?",
      message: `Every verified Emergency Response Report and Nonconforming Product Report for this site between ${bulkPeriodStart} and ${bulkPeriodEnd} will be filed as its own vault snapshot (skipping any already filed).`,
      confirmLabel: "File incidents",
    });
    if (!ok) return;
    setVaultBusy(true);
    try {
      const { data: res } = await api.post("/audit-vault/bulk-file-incidents", {
        site_id: siteId,
        start_period: bulkPeriodStart,
        end_period: bulkPeriodEnd,
      });
      const totalFiled = (res.err?.filed || 0) + (res.ncr?.filed || 0);
      const totalDup   = (res.err?.duplicate || 0) + (res.ncr?.duplicate || 0);
      if (totalFiled === 0 && totalDup > 0) {
        await notify({
          title: "Nothing new to file",
          message: `All ${totalDup} incident(s) in this window are already in the Audit Vault.`,
          variant: "warn",
        });
      } else if (totalFiled === 0) {
        await notify({
          title: "No incidents to file",
          message: "No verified ERR or NCR records exist for this site in the selected window. Verify the incident first, then re-run this action.",
          variant: "warn",
        });
      } else {
        await notify({
          title: "Incidents filed",
          message: `Filed ${totalFiled} snapshot(s) (${res.err?.filed || 0} ERR · ${res.ncr?.filed || 0} NCR)${totalDup ? ` — skipped ${totalDup} already-filed` : ""}. Open the Audit Vault to sign them.`,
          variant: "ok",
        });
      }
      onFiled?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = (det && typeof det === "object") ? det.code : null;
      await notify({
        title: code === "signature_required" ? "Signature required" : "Couldn't file incidents",
        message: (det && typeof det === "object") ? det.message
          : (typeof det === "string" ? det : "Try again from the Audit Vault."),
        variant: code === "signature_required" ? "warn" : "err",
      });
    } finally { setVaultBusy(false); }
  };

  const fileBulkPest = async () => {
    if (!canFile || vaultBusy) return;
    const ok = await confirm({
      title: "File Pest Control in the Audit Vault?",
      message: `Pest Control Period Report (${bulkPeriodStart} → ${bulkPeriodEnd}) will be filed for this site — bait layout, vendor docs and every monthly visit report merged into one PDF. Filed independently from Approved Vendor so each can be sent for rework on its own.`,
      confirmLabel: "File Pest Control",
    });
    if (!ok) return;
    setVaultBusy(true);
    try {
      const { data: res } = await api.post("/audit-vault/bulk-file-pest", {
        site_id: siteId,
        start_period: bulkPeriodStart,
        end_period: bulkPeriodEnd,
      });
      if (res.pest_period?.kind === "filed") {
        await notify({
          title: "Pest Control filed",
          message: "Filed the Pest Control Period Report in the Audit Vault. Open the Vault to sign as generator.",
          variant: "ok",
        });
      } else {
        await notify({
          title: "Couldn't file Pest Control",
          message: res.pest_period?.message || res.pest_period?.kind || "Try again from the Audit Vault.",
          variant: "warn",
        });
      }
      onFiled?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = (det && typeof det === "object") ? det.code : null;
      await notify({
        title: code === "signature_required" ? "Signature required" : "Couldn't file Pest Control",
        message: (det && typeof det === "object") ? det.message
          : (typeof det === "string" ? det : "Try again from the Audit Vault."),
        variant: code === "signature_required" ? "warn" : "err",
      });
    } finally { setVaultBusy(false); }
  };

  const fileBulkVendor = async () => {
    if (!canFile || vaultBusy) return;
    const ok = await confirm({
      title: "File Approved Vendor Dossier in the Audit Vault?",
      message: "A tenant-wide standing snapshot of every approved vendor will be filed — one full section per vendor with evaluation scores and uploaded documents merged in. Filed independently from Pest Control so each can be sent for rework on its own.",
      confirmLabel: "File Vendor Dossier",
    });
    if (!ok) return;
    setVaultBusy(true);
    try {
      const { data: res } = await api.post("/audit-vault/bulk-file-vendor", {});
      if (res.vendor_list?.kind === "filed") {
        await notify({
          title: "Vendor Dossier filed",
          message: "Filed the Approved Vendor Dossier in the Audit Vault. Open the Vault to sign as generator.",
          variant: "ok",
        });
      } else {
        await notify({
          title: "Couldn't file Vendor Dossier",
          message: res.vendor_list?.message || res.vendor_list?.kind || "Try again from the Audit Vault.",
          variant: "warn",
        });
      }
      onFiled?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = (det && typeof det === "object") ? det.code : null;
      await notify({
        title: code === "signature_required" ? "Signature required" : "Couldn't file Vendor Dossier",
        message: (det && typeof det === "object") ? det.message
          : (typeof det === "string" ? det : "Try again from the Audit Vault."),
        variant: code === "signature_required" ? "warn" : "err",
      });
    } finally { setVaultBusy(false); }
  };

  // DRAFT preview — opens the report in a new tab with a
  // "DRAFT — NOT FOR AUDIT" watermark so the owner can eyeball the
  // shape of the report BEFORE the month closes or gaps are plugged.
  // Only wired for pillars that have a preview endpoint mapped above.
  const previewEndpoint = PREVIEW_ENDPOINT_FOR_PILLAR[pillar.key];
  const canPreview = !!(previewEndpoint && siteId && periodMonthForVault);
  const [previewBusy, setPreviewBusy] = useState(false);
  const openPreview = async () => {
    if (!canPreview || previewBusy) return;
    setPreviewBusy(true);
    try {
      // Fetch with the current auth header, then open the returned
      // blob in a new tab. Avoids relying on ?auth=<token> tacked onto
      // an anchor href which some browsers strip during navigation.
      const res = await api.get(previewEndpoint, {
        params: {
          site_id: siteId,
          period_type: "month",
          period_value: periodMonthForVault,
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Give the tab time to grab the blob before we revoke.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      // When responseType is "blob", axios stores the server's JSON
      // error body as a Blob too — read it back to surface the real
      // reason (e.g. "No CCP equipment registered for the selected
      // site — add CCP equipment first.") instead of a generic message.
      let msg = "Couldn't render the DRAFT preview.";
      try {
        const body = e?.response?.data;
        if (body instanceof Blob) {
          const text = await body.text();
          try {
            const j = JSON.parse(text);
            msg = (typeof j?.detail === "string" ? j.detail : msg);
          } catch { /* not JSON — fall through */ }
        } else if (typeof body?.detail === "string") {
          msg = body.detail;
        }
      } catch { /* keep default */ }
      await notify({
        title: "Preview failed",
        message: msg,
        variant: "err",
      });
    } finally { setPreviewBusy(false); }
  };

  const fileInVault = async () => {
    if (!canFile || vaultBusy) return;
    if (isBulkIncidents) { await fileBulkIncidents(); return; }
    if (isBulkPest) { await fileBulkPest(); return; }
    if (isBulkVendor) { await fileBulkVendor(); return; }
    const isRefile = isStanding && inVault;
    const ok = await confirm({
      title: isRefile ? "File a fresh report?" : "File in Audit Vault?",
      message: isRefile
        ? `A new ${pillar.label} report will be filed alongside the previous one. Both remain auditable; the latest reflects today's state.`
        : `A "${pillar.label}" report${
            isStanding ? " for today" : ` for ${periodValue || "the current period"}`
          } will be created. You'll sign it as the generator next.`,
      confirmLabel: isRefile ? "File new report" : "Create Report",
    });
    if (!ok) return;
    setVaultBusy(true);
    try {
      const payload = { report_type: vaultType, site_id: siteId };
      // Periodic types need period_month; standing types don't.
      if (!isStanding) payload.period_month = periodMonthForVault;
      await api.post("/audit-vault/generate", payload);
      await notify({
        title: "Snapshot created",
        message: "Filed in the Audit Vault — open it later to sign as generator. You can keep filing other pillars from here.",
        variant: "ok",
      });
      onFiled?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = (det && typeof det === "object") ? det.code : null;
      if (code === "duplicate") {
        await notify({ title: "Already in the vault", message: det.message, variant: "warn" });
      } else if (code === "signature_required") {
        await notify({ title: "Signature required", message: det.message, variant: "warn" });
      } else if (code === "incomplete_data") {
        await notify({ title: "Cannot freeze yet", message: det.message || "Data still incomplete.", variant: "warn" });
      } else {
        await notify({
          title: "Couldn't file in vault",
          message: typeof det === "string" ? det : "Try again from the Reports tile.",
          variant: "err",
        });
      }
    } finally { setVaultBusy(false); }
  };

  // Button copy varies by pillar type so the affordance reads naturally.
  const fileButtonLabel = isBulkIncidents
    ? (vaultBusy ? "Filing incidents…" : "File all in Vault")
    : isBulkPest
      ? (vaultBusy ? "Filing Pest Control…" : "File Pest Control")
      : isBulkVendor
        ? (vaultBusy ? "Filing Vendors…" : "File Vendor Dossier")
        : (isStanding && inVault)
          ? (vaultBusy ? "Filing…" : "Re-file snapshot")
          : (vaultBusy ? "Filing…" : "File in Vault");

  return (
    <div
      className="vb-card p-4 transition-shadow hover:shadow-md flex flex-col"
      data-testid={`pillar-card-${pillar.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider opacity-60">
            {pillar.form_ref || ""}
          </div>
          <h3 className="vb-heading text-base truncate" style={{ color: "var(--vb-text-2)" }}>
            {pillar.label}
          </h3>
        </div>
        <span className={`vb-pill ${meta.pill}`} data-testid={`pillar-status-${pillar.key}`}>
          {meta.label}
        </span>
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span
          className="vb-heading text-2xl"
          style={{ color: meta.color }}
          data-testid={`pillar-score-${pillar.key}`}
        >
          {pillar.score?.toFixed?.(1) ?? pillar.score}%
        </span>
      </div>

      <p className="text-sm opacity-80 leading-snug">{pillar.headline}</p>

      {pillar.details?.length > 0 && (
        <ul className="text-xs opacity-70 grid gap-1 mt-1 pl-4 list-disc">
          {pillar.details.slice(0, 3).map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      )}

      {/* Footer: pinned to the bottom of the card via mt-auto so every
          tile lines up visually regardless of how tall its body is.
          One row, two zones — left = "Open module" link, right = file
          button + in-vault pill. Wraps gracefully on narrow widths. */}
      <div className="flex items-center gap-2 flex-wrap justify-between mt-auto pt-3 border-t"
        style={{ borderColor: "rgba(0,0,0,0.06)", marginTop: "auto" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {pillar.cta_path && (
            <Link
              to={pillar.cta_path}
              className="inline-flex items-center gap-1 text-xs font-semibold hover:underline whitespace-nowrap"
              style={{ color: "var(--vb-text-2)" }}
              data-testid={`pillar-cta-${pillar.key}`}
            >
              Open module <ArrowUpRight size={12} />
            </Link>
          )}
          {!canFile && !inVault && vaultType && pillar.status !== "ready" && (
            <span className="text-[10px] opacity-50 inline-flex items-center gap-1 whitespace-nowrap"
              title="The Audit Vault only accepts 100%-ready snapshots so reports stay audit-grade.">
              <ExternalLink size={10} /> Reach 100% to file
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* In-vault status pill (compact, never alone on a row) */}
          {isBulk && inVault && (
            <Link
              to="/app/audit-vault"
              className="vb-pill ok cursor-default whitespace-nowrap"
              style={{ fontSize: 10, padding: "3px 8px" }}
              title="Open the Audit Vault to sign the filed snapshots."
              data-testid={`pillar-in-vault-${pillar.key}`}
            >
              <ShieldCheck size={10} />
              {isBulkIncidents
                ? `${vs.filed_count || 0} in Vault`
                : isBulkPest
                  ? `${vs.pest_count || 0} in Vault`
                  : `${vs.vendor_count || 0} in Vault`}
            </Link>
          )}
          {!isBulk && inVault && (
            <Link
              to="/app/audit-vault"
              className="vb-pill ok cursor-default whitespace-nowrap"
              style={{ fontSize: 10, padding: "3px 8px" }}
              title={
                vs.status === "approved"
                  ? "Approved & locked. Open the Audit Vault to download."
                  : vs.status === "awaiting_approval"
                  ? "Snapshot filed, awaiting approver signature."
                  : vs.status === "awaiting_generator_sig"
                  ? "Snapshot filed, awaiting generator signature."
                  : vs.status === "rejected"
                  ? "Previous snapshot was returned for revision — open the Vault to regenerate."
                  : "Already in the Audit Vault."
              }
              data-testid={`pillar-in-vault-${pillar.key}`}
            >
              <ShieldCheck size={10} />
              {vs.status === "approved" ? "Locked"
                : vs.status === "awaiting_approval" ? "Approval"
                : vs.status === "awaiting_generator_sig" ? "Signature"
                : vs.status === "rejected" ? "Rejected"
                : "Vault"}
            </Link>
          )}
          {canPreview && (
            <button
              type="button"
              className="vb-btn ghost xs"
              onClick={openPreview}
              disabled={previewBusy}
              title="Download a DRAFT PDF (with watermark) for review — useful when the month is still open or the pillar isn't yet 100%. Not audit-grade."
              data-testid={`pillar-preview-${pillar.key}`}
            >
              <FileText size={11} />
              {previewBusy ? "Loading…" : "Preview PDF"}
            </button>
          )}
          {(canFile || !isReady) && (
            <button
              type="button"
              className="vb-btn xs"
              onClick={fileInVault}
              disabled={vaultBusy || !canFile}
              title={
                !isReady
                  ? `Pillar is at ${pillar.score?.toFixed?.(1) ?? pillar.score}%. Fix the underlying data (open the module below) until it reaches 100%, then re-run the assessment to file an audit-clean snapshot.`
                  : isBulkIncidents
                    ? "Sweep every verified ERR + NCR in the selected window and file each one as a vault snapshot."
                    : isBulkPest
                      ? "File the Pest Control Period Report for the selected window."
                      : isBulkVendor
                        ? "File the Approved Vendor Dossier (tenant-wide standing snapshot)."
                        : isStanding && inVault
                          ? "Create a fresh snapshot alongside the previous one."
                          : "Freeze this pillar's snapshot and start the signature workflow in the Audit Vault."
              }
              data-testid={`pillar-file-${pillar.key}`}
            >
              <ShieldCheck size={11} />
              {!isReady ? "Fix gaps first" : fileButtonLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
